/* eslint-disable no-nested-ternary */
// React et hooks
import { useEffect, useContext, useMemo, useState, useCallback, useRef } from "react";

// MUI
import { Box, Paper, Button, Typography, useTheme, Skeleton, Stack, IconButton, ClickAwayListener, Popper, styled } from "@mui/material";
import { DataGridPro, GridColDef, GridToolbar, GridSortModel, useGridApiRef, useGridApiContext } from '@mui/x-data-grid-pro';

// Redux
import { useDispatch, useSelector } from "react-redux";
import { subDays } from 'date-fns';
import { setFilterCompare, setFilterDate, setFilterDateCompare } from 'src/context/reducers/filter-settings/filterSlice';
import { Helmet } from 'react-helmet-async';
// Hooks personnalisés
import { useResponsiveConfig } from "src/components/visibility/utils/use-responsive-config";

// Context et reducers
import { useCurrentWorkspace } from "src/context/reducers/app-settings";

// API queries
import { useGetVisibilityKpisQuery, useGetOverviewVisibilityCardQuery } from "src/context/api/competitorsMonitoring/api";

// Localisation
import { useLocales } from "src/locales";

// Composants
import { SplashScreen } from 'src/components/loading-screen';
import PresenceRateChart from 'src/components/visibility/components/PresenceRateChart';
import { CardBar } from 'src/components/visibility/components/CardBar';
import type { TopCompetitorStat } from 'src/components/visibility/components/TopCompetitorsCard';
// Utilitaires
import { fDate } from "src/utils/format-time";
import countriesJson from "src/utils/countries.json";
import { useNavigate } from 'react-router-dom';
import { dashboardPaths } from 'src/routes/dashboardPaths';
// Styles (non-brand card layout: card-top-header, card-footer, etc.)
import '../non-branded/_style.scss';
import "src/components/visibility/style.css";

// Context centralisé
import { VisibilityContext } from "src/components/visibility/components/VisibilityProvider";


import {
    mapLevelToLabel,
    coverageToLevel,
} from "src/components/visibility/utils";
import { PresenceSegments } from "src/components/visibility/types";
import { createDataGridColumns } from "src/components/visibility/utils/datagrid-config";
import Iconify from "src/components/iconify";

export const exposedToTest = (level: string): string => mapLevelToLabel(level)



export default function Overview() {
    const { t } = useLocales();
    const theme = useTheme();
    const navigate = useNavigate();

    // Configuration responsive granulaire
    const responsiveConfig = useResponsiveConfig();

    // Essayer d'utiliser le contexte centralisé si disponible
    const context = useContext(VisibilityContext);

    // Fallback pour l'utilisation standalone
    const standaloneWorkspace: any = useCurrentWorkspace();
    const standaloneFilterStore = useSelector((state: any) => state.filters);

    // Utiliser le contexte s'il est disponible, sinon les valeurs standalone
    const workspace = context?.workspace || standaloneWorkspace;
    const filterStore = context?.filterStore || standaloneFilterStore;

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(setFilterDate({
            start: subDays(new Date(), 30).toISOString(),
            end: subDays(new Date(), 1).toISOString(),
        }));
        dispatch(setFilterCompare(false));
        dispatch(setFilterDateCompare({
            compareStart: subDays(new Date(), 60).toISOString(),
            compareEnd: subDays(new Date(), 31).toISOString(),
        }));
    }, []);

    // Fetch competitive intelligence data uniquement si pas de contexte
    const skipQuery = context ? true : (!filterStore.start || !filterStore.end || !workspace?.id);

    const { data: standaloneVisibilityData, isFetching: standaloneIsFetching } = useGetVisibilityKpisQuery({
        startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
        endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
        compareStartDate: filterStore.compareStart !== undefined ? fDate(filterStore.compareStart, "yyyy-MM-dd") : null,
        compareEndDate: filterStore.compareEnd !== undefined ? fDate(filterStore.compareEnd, "yyyy-MM-dd") : null,
        timestep: filterStore.timestep,
        devices: filterStore.devices,
        countries: filterStore.countries,
        keywords: filterStore.keywords,
        platforms: filterStore?.platforms,
        workspaceId: workspace?.id,
    }, {
        skip: skipQuery,
    });

    // Utiliser les données du contexte ou les données standalone
    const visibilityData = context?.visibilityData ||
        (Array.isArray(standaloneVisibilityData) ? standaloneVisibilityData : []);
    const isFetchingVisibility = context?.isLoadingBaseData || standaloneIsFetching;

    const { data: overviewVisibilityCardData, isFetching: isFetchingOverviewVisibilityCard } = useGetOverviewVisibilityCardQuery({
        startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
        endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
        devices: filterStore.devices || [],
        countries: filterStore.countries || [],
        keywords: filterStore.keywords || [],
        platforms: filterStore?.platforms || [],
        workspaceId: workspace?.id,
    }, {
        skip: !filterStore.start || !filterStore.end || !workspace?.id,
    });

    // Les filtres sont déjà initialisés dans le Redux store, pas besoin de les réinitialiser

    // Controlled sorting with error handling for rapid page switching
    const apiRef: any = useGridApiRef();
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'country', sort: 'desc' }]);
    const [hasError, setHasError] = useState(false);

    // Cleanup apiRef on component unmount to prevent ARIA errors during rapid switching
    // This addresses the useGridRowAriaAttributes error when switching between overview and search-term-analysis
    useEffect(() => () => {
        // Reset sort model to clean up DataGrid state before unmounting
        if (apiRef.current) {
            try {
                apiRef.current.setSortModel([]);
            } catch (error) {
                // Silently ignore cleanup errors to prevent console spam
            }
        }
    }, [apiRef]);

    const toggleTrafficShareSort = () => {
        try {
            // Get current sort state and toggle direction
            const currentModel = (apiRef.current?.getSortModel?.() as GridSortModel | undefined) || sortModel;
            const current = currentModel[0];
            const currentDir = current && current.field === 'country' ? current.sort : sortModel[0]?.sort;
            const nextDir = currentDir === 'desc' ? 'asc' : 'desc';
            const nextModel: GridSortModel = [{ field: 'country', sort: nextDir }];

            // Apply new sort model to both apiRef and local state
            apiRef.current?.setSortModel?.(nextModel);
            setSortModel(nextModel);
            setHasError(false);
        } catch (error) {
            // Handle errors gracefully during rapid switching
            console.warn('Error in toggleTrafficShareSort:', error);
            setHasError(true);
        }
    };

    // Create columns for the DataGrid using externalized configuration
    const columns: GridColDef[] = createDataGridColumns({
        responsiveConfig,
        theme,
        t,
        workspace,
        sortModel,
        toggleTrafficShareSort,
        PresenceRateChart,
        columnType: 'country'
    });

    const rows = (visibilityData as any[] | undefined)?.map((item: any, index: number) => {
        const allCompetitors = Array.isArray(item?.top_5_competitors) ? item.top_5_competitors : [];
        const firstCompetitor = allCompetitors[0];
        const otherCompetitors = allCompetitors.slice(1);
        const filteredCompetitors = firstCompetitor ? [firstCompetitor, ...otherCompetitors] : otherCompetitors;

        const topCompetitors = filteredCompetitors.map((c: any) => c?.domain_name).filter(Boolean);
        const topCompetitorsPaidRates: Record<string, number> = filteredCompetitors.reduce((acc: Record<string, number>, c: any) => {
            const domain = c?.domain_name;
            const rate = c?.paid_presence_rate;
            if (domain && typeof rate === 'number') acc[domain] = rate;
            return acc;
        }, {});

        const firstCompetitorDomain = filteredCompetitors.length > 0 && filteredCompetitors[0]?.domain_name
            ? filteredCompetitors[0].domain_name
            : undefined;

        return {
            id: item?.country_name ?? index,
            country: item?.country_name ?? '',
            trafficShare: item?.brand_traffic_share ?? 0,
            competitionEvolution: mapLevelToLabel(item?.level ?? coverageToLevel(item?.coverage)),
            levelRaw: (item?.level ?? coverageToLevel(item?.coverage)) || undefined,
            brandDomain: workspace?.url,
            firstCompetitorDomain,
            isDemoBrand: workspace?.name?.includes('Demo'),
            topCompetitors,
            topCompetitorsPaidRates,
            blendedPresenceRate: item?.blended_presence_rate ?? 0,
            // Brand and first competitor paid presence rates.
            // Fallbacks allow consumption of main_overview.sql fields directly.
            paidPresenceRate:
                item?.client_paid_presence_rate
                ?? ((item?.solo_bidder ?? 0) + (item?.top_ad_position ?? 0)),
            firstCompetitorPaidPresenceRate:
                item?.first_compet_paid_presence_rate
                ?? (typeof filteredCompetitors?.[0]?.paid_presence_rate === 'number'
                    ? filteredCompetitors[0].paid_presence_rate
                    : 0),
            presenceRateData: {
                organicOnly: item?.organic_only ?? 0,
                soloBidder: item?.solo_bidder ?? 0,
                topAdPosition: item?.top_ad_position ?? 0,
            } as PresenceSegments,
            // Exposed for upcoming Signal Coverage card integration.
            signalCoverage: item?.coverage ?? null,
            status: item?.status,
        };
    }) ?? [];

    const topCompetitorsData = useMemo<TopCompetitorStat[]>(() => {
        const firstItem = (visibilityData as any[] | undefined)?.[0];
        const top3FromBackend =
            firstItem?.top_3_competitors
            ?? firstItem?.top_3_compet
            ?? firstItem?.card_top_3_competitors;

        if (Array.isArray(top3FromBackend) && top3FromBackend.length > 0) {
            return top3FromBackend
                .map((item: any) => ({
                    domain: String(item?.domain_name ?? item?.domain ?? '').trim(),
                    paidPresenceRate:
                        typeof item?.paid_presence_rate === 'number'
                            ? item.paid_presence_rate
                            : 0,
                }))
                .filter((item: TopCompetitorStat) => item.domain.length > 0)
                .sort((a: TopCompetitorStat, b: TopCompetitorStat) => b.paidPresenceRate - a.paidPresenceRate)
                .slice(0, 3);
        }

        const stats = new Map<string, { total: number; count: number }>();

        // Get workspace domains to exclude them from competitors
        const workspaceDomains = new Set<string>();
        if (workspace?.url) {
            workspaceDomains.add(workspace.url.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, ''));
        }
        if (workspace?.friendlyDomains && Array.isArray(workspace.friendlyDomains)) {
            workspace.friendlyDomains.forEach((d: string) => {
                const normalized = d.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '');
                workspaceDomains.add(normalized);
            });
        }

        (visibilityData as any[] | undefined)?.forEach((item: any) => {
            if (!Array.isArray(item?.top_5_competitors)) return;
            item.top_5_competitors.forEach((competitor: any) => {
                const domain = competitor?.domain_name;
                const rate = competitor?.paid_presence_rate;
                if (!domain || typeof rate !== 'number') return;

                // Normalize domain for comparison
                const normalizedDomain = domain.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '');
                if (workspaceDomains.has(normalizedDomain)) return; // Skip workspace domains

                const current = stats.get(domain) || { total: 0, count: 0 };
                current.total += rate;
                current.count += 1;
                stats.set(domain, current);
            });
        });

        return Array.from(stats.entries())
            .map(([domain, { total, count }]) => ({
                domain: domain.trim(),
                paidPresenceRate: count ? Math.round((total / count) * 10) / 10 : 0,
            }))
            .filter(item => item.domain.length > 0)
            .sort((a, b) => b.paidPresenceRate - a.paidPresenceRate)
            .slice(0, 3);
    }, [visibilityData, workspace]);

    // Extract weekly series data from dedicated visibility card SQL
    const blendedPresenceRateWeeklySeries = useMemo(() => {
        const visibilityData = overviewVisibilityCardData?.data;
        if (Array.isArray(visibilityData?.blended_presence_rate_weekly_series)) {
            return visibilityData.blended_presence_rate_weekly_series;
        }

        const firstItem = (visibilityData as any[] | undefined)?.[0];
        const weeklySeries =
            firstItem?.blended_presence_rate_weekly_series
            ?? firstItem?.visibility_rate_weekly_series
            ?? firstItem?.weekly_visibility_rate_series;

        if (Array.isArray(weeklySeries)) {
            return weeklySeries;
        }
        return undefined;
    }, [overviewVisibilityCardData?.data, visibilityData]);

    // Solo bidder rate card: value from last week, series from visibility card
    const soloBidderWeeklySeries = useMemo(() => {
        const series = blendedPresenceRateWeeklySeries;
        if (!Array.isArray(series)) return undefined;
        return series.map((d: { week_start?: string; solo_bidder?: number }) => ({
            week_start: d?.week_start ?? '',
            solo_bidder: typeof d?.solo_bidder === 'number' ? d.solo_bidder : 0,
        }));
    }, [blendedPresenceRateWeeklySeries]);

    // Main KPI for solo_bidder card: use total_weekly_solo_bidder_rate from visibility card API
    const soloBidderValue = useMemo(() => {
        const visibilityData = overviewVisibilityCardData?.data;
        const total = visibilityData?.total_weekly_solo_bidder_rate;
        if (typeof total === 'number' && !Number.isNaN(total)) return total;
        const series = blendedPresenceRateWeeklySeries;
        if (!Array.isArray(series) || series.length === 0) return undefined;
        const last = series[series.length - 1];
        const v = last?.solo_bidder;
        return typeof v === 'number' && !Number.isNaN(v) ? v : undefined;
    }, [overviewVisibilityCardData?.data, blendedPresenceRateWeeklySeries]);

    // Calculate top metric (blended presence rate) as average over all countries data
    const blendedPresenceTopMetric = useMemo(() => {
        const visibilityData = overviewVisibilityCardData?.data;
        const dedicatedTotal = visibilityData?.total_weekly_visibility_rate;
        if (typeof dedicatedTotal === 'number' && !isNaN(dedicatedTotal)) {
            return `${Math.round(dedicatedTotal)}%`;
        }

        const items = Array.isArray(visibilityData) ? visibilityData : [];
        const firstItem = items[0];

        const totalFromBackend =
            firstItem?.total_weekly_visibility_rate
            ?? firstItem?.total_visibility_rate
            ?? firstItem?.blended_presence_top_metric;

        if (typeof totalFromBackend === 'number' && !isNaN(totalFromBackend)) {
            return `${Math.round(totalFromBackend)}%`;
        }

        const validRates = items
            .map((item: any) => item?.blended_presence_rate)
            .filter((rate): rate is number => typeof rate === 'number' && rate > Math.round(0));
        if (validRates.length > 0) {
            const averageRate = validRates.reduce((sum: number, rate: number) => sum + rate, 0) / validRates.length;
            return `${Math.round(averageRate)}%`;
        }
        return undefined;
    }, [overviewVisibilityCardData?.data, visibilityData]);

    useEffect(() => {
        if (process.env.NODE_ENV === 'production') return;
        const firstItem = (visibilityData as any[] | undefined)?.[0];
    }, [visibilityData, blendedPresenceTopMetric]);

    // État de chargement pour les cartes : on ne montre les skeletons que
    // lors du premier chargement pour un workspace donné.
    const [hasLoadedCardsOnce, setHasLoadedCardsOnce] = useState(false);

    useEffect(() => {
        // Reset lorsque le workspace change
        setHasLoadedCardsOnce(false);
    }, [workspace?.id]);

    useEffect(() => {
        if (!workspace?.id) return;
        if (hasLoadedCardsOnce) return;
        if (!isFetchingVisibility && !isFetchingOverviewVisibilityCard) {
            setHasLoadedCardsOnce(true);
        }
    }, [workspace?.id, isFetchingVisibility, isFetchingOverviewVisibilityCard, hasLoadedCardsOnce]);

    const cardBarLoading = !hasLoadedCardsOnce && (isFetchingVisibility || isFetchingOverviewVisibilityCard);

    if (!filterStore?.countries || !filterStore?.keywords || !filterStore?.devices) {
        return <SplashScreen />;
    }

    const CustomToolbar = (props: any) => {
        return (
            <Stack
                ref={toolbarRef}
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={0}
                px={2.5}
                py={1.25}
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    backgroundColor: 'white',
                    borderTopRightRadius: '8px',
                    borderTopLeftRadius: '8px',
                    borderBottom: '1px dashed rgba(145, 158, 171, 0.20)',
                }}
            >

                <Stack>
                    <Typography fontWeight={600} fontSize={"16px"} color={"#333C47"}>Countries</Typography>
                </Stack>

                <Stack direction="row" alignItems="center" px={1} spacing={0.5}>
                    <IconButton
                        onClick={() => apiRef.current?.showFilterPanel()}
                        sx={{
                            width: 26,
                            height: 26,
                            padding: 0,
                            color: '#919EAB',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            }
                        }}
                    >
                        <Iconify icon="ic:round-filter-list" width={18} height={18} />
                    </IconButton>
                </Stack>
            </Stack>
        )
    }

    const toolbarRef: any = useRef();

    const GridPanelRoot = styled(Popper, {
        name: 'MuiDataGrid',
        slot: 'Panel',
        overridesResolver: (props, styles) => styles.panel
    })(({
        theme
    }) => ({
        zIndex: (theme as any).zIndex.modal
    }));
    const GridPaperRoot = styled(Paper, {
        name: 'MuiDataGrid',
        slot: 'Paper',
        overridesResolver: (props, styles) => styles.paper
    })(({
        theme
    }) => ({
        backgroundColor: "#FFFFFF",
        minWidth: 300,
        maxHeight: 450,
        display: 'flex',
        maxWidth: `calc(100vw - ${theme.spacing(0.5)})`,
        overflow: 'auto'
    }));

    return (

        <Box sx={{ px: "24px" }}>
            <Helmet>
                <title>Overview</title>
            </Helmet>

            <CardBar
                blendedPresenceTitle="Visibility Rate"
                blendedPresenceTopMetric={blendedPresenceTopMetric}
                blendedPresenceWeeklySeries={blendedPresenceRateWeeklySeries}
                soloBidderTitle="Solo bidder rate"
                soloBidderValue={soloBidderValue}
                soloBidderWeeklySeries={soloBidderWeeklySeries}
                topCompetitorsTitle="Top Competitors"
                topCompetitorsData={topCompetitorsData}
                topCompetitorsRightLabel={t('dashboard.visibility.paidPresenceRate')}
                dateRange="12 weeks"
                loading={cardBarLoading}
                useNonBrandLayout
            />
            <Paper sx={{ p: 0, overflow: 'hidden', mt: 2 }}>
                <Box
                    sx={{
                        zIndex: -100,
                        width: '100%',
                        // Enhanced border visibility for light mode - lighter borders
                        border: `1px solid  rgba(145, 158, 171, 0.20)`,
                        borderRadius: "12px",
                        overflow: 'hidden',
                        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
                            outline: 'none !important',
                        },
                        '& .MuiDataGrid-cell': {
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            // Override des bordures dashed par des bordures solides - lighter
                            borderBottom: `1px solid ${theme.palette.mode === 'dark' ? '#919eab33' : '#f0f0f0'} !important`,
                        },
                        '& .MuiDataGrid-row': {
                            alignItems: 'center',
                        },
                        '& .MuiDataGrid-columnHeader': {
                            display: 'flex',
                            alignItems: 'center',
                            // Enhanced header border visibility - lighter
                            borderBottom: `1px solid ${theme.palette.mode === 'dark' ? '#919eab33' : '#f0f0f0'} !important`,
                        },
                        // Masquer la flèche de tri native sur la colonne country (même au hover)
                        '& .MuiDataGrid-columnHeader[data-field="country"] .MuiDataGrid-sortIcon': {
                            display: 'none !important',
                        },
                        '& .MuiDataGrid-columnHeader[data-field="country"]:hover .MuiDataGrid-sortIcon': {
                            display: 'none !important',
                        },
                        '& .MuiDataGrid-cellContent': {
                            display: 'flex',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                        },
                        '& .row-not-scrapped .MuiDataGrid-cell': {
                            color: theme.palette.text.disabled,
                        },
                    }}
                >
                    {isFetchingVisibility ? <Skeleton variant="rectangular" height={500} /> : hasError ? (
                        // Error fallback UI for DataGrid rendering issues during rapid switching
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="h6" color="error" gutterBottom>
                                {t('dashboard.visibility.dataGridError.title')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {t('dashboard.visibility.dataGridError.message')}
                            </Typography>
                            <Button
                                variant="outlined"
                                onClick={() => setHasError(false)}
                            >
                                {t('dashboard.visibility.dataGridError.retry')}
                            </Button>
                        </Box>
                    ) : (
                        <Box sx={{
                            "& .MuiDataGrid-columnHeader": {
                                backgroundColor: 'rgba(226, 229, 233, 0.16)!important'
                            },
                            "& .MuiDataGrid-columnHeaderTitleContainerContent": {
                                color: 'rgba(99, 115, 129, 1)!important'
                            },
                            "& .MuiDataGrid-cell": {
                                border: "1px solid rgba(145, 158, 171, 0.20)"
                            }
                        }}>
                            <DataGridPro
                                // Unique key forces complete remount on workspace change, preventing ARIA conflicts
                                key={`overview-datagrid-${workspace?.id || 'default'}`}
                                apiRef={apiRef}
                                autoHeight
                                disableDensitySelector
                                disableColumnReorder
                                disableColumnMenu
                                checkboxSelection={false}
                                disableRowSelectionOnClick
                                sortingMode="client"
                                disableMultipleColumnsSorting
                                onCellClick={(params, event) => {
                                    try {
                                        if (params.field === 'country') {
                                            const countryName = params.row?.country ?? '';
                                            // Persist country in context for other CI pages
                                            context?.setSelectedCountry?.(countryName);
                                            // Navigate with ISO code (e.g., FR) for deep linking
                                            const code = countriesJson?.find((c) => c.name === countryName)?.code || countryName;
                                            navigate(`${dashboardPaths.competitiveSearchTermAnalysis}?country=${encodeURIComponent(String(code).toUpperCase())}`);
                                        }
                                    } catch (error) {
                                        // Handle navigation errors during rapid switching
                                        console.warn('Error in onCellClick:', error);
                                    }
                                }}
                                rows={rows}
                                columns={columns}
                                getRowClassName={(params) => (params.row?.status === 'not scrapped' ? 'row-not-scrapped' : '')}
                                sortingOrder={['desc', 'asc']}
                                sortModel={sortModel}
                                onSortModelChange={(newModel) => {
                                    try {
                                        // Update local sort state, handled by DataGrid internally
                                        setSortModel(newModel);
                                    } catch (error) {
                                        // Handle sort model errors during rapid switching
                                        console.warn('Error in onSortModelChange:', error);
                                    }
                                }}

                                pagination
                                showCellVerticalBorder
                                pageSizeOptions={[10, 20, 50, 100]}
                                initialState={{
                                    pagination: {
                                        paginationModel: { pageSize: 10, page: 0 },
                                    },
                                }}
                                slots={{
                                    toolbar: CustomToolbar,
                                    panel(props) {
                                        const apiRefContext = useGridApiContext();
                                        const [isPlaced, setIsPlaced] = useState(false);

                                        const handleClickAway = useCallback(() => {
                                            apiRefContext.current.hidePreferences();
                                        }, [apiRefContext]);

                                        const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
                                            if (event.key === 'Escape') {
                                                apiRefContext.current.hidePreferences();
                                            }
                                        }, [apiRefContext]);

                                        const modifiers = useMemo(() => [{
                                            name: 'flip',
                                            enabled: true,
                                            options: {
                                                rootBoundary: 'document'
                                            }
                                        }, {
                                            name: 'isPlaced',
                                            enabled: true,
                                            phase: 'main' as const,
                                            fn: () => {
                                                setIsPlaced(true);
                                            },
                                            effect: () => () => {
                                                setIsPlaced(false);
                                            }
                                        }] as any, []);

                                        const open = apiRef.current?.state?.preferencePanel?.open;
                                        const panelType = apiRef.current?.state?.preferencePanel?.openedPanelValue;
                                        const anchorEl = toolbarRef.current;

                                        if (!anchorEl) {
                                            return null;
                                        }

                                        const panelProps = {
                                            ...props,
                                            as: undefined,
                                            anchorEl: anchorEl,
                                            open: open,
                                        };

                                        return (
                                            <GridPanelRoot
                                                {...panelProps}
                                                placement="bottom-end"
                                                className={props.className}
                                                modifiers={modifiers}
                                            >
                                                <ClickAwayListener
                                                    mouseEvent="onPointerUp"
                                                    touchEvent={false}
                                                    onClickAway={handleClickAway}
                                                >
                                                    <GridPaperRoot
                                                        elevation={8}
                                                        onKeyDown={handleKeyDown}
                                                    >
                                                        {isPlaced && props.children}
                                                    </GridPaperRoot>
                                                </ClickAwayListener>
                                            </GridPanelRoot>
                                        );
                                    },

                                }}
                                slotProps={{
                                    toolbar: {
                                        printOptions: { disableToolbarButton: true },
                                        csvOptions: { disableToolbarButton: true }
                                    },
                                }}
                            />
                        </Box>
                    )}
                </Box>
            </Paper>
        </Box>
    );
}
